/*
 * <pagination-component> powers the "Load More" and "Infinite Scroll"
 * pagination styles for the collection product grid.
 *
 * It never invents pages or products: every fetch walks Shopify's real
 * pagination (paginate.next.url, which already carries the active filters,
 * theme chips and sorting) and appends the returned products to the grid,
 * de-duplicated by product id.
 *
 * Source of truth for the numbers:
 *   - total  = paginate.items from Shopify (data-total), i.e. the number of
 *              products matching the active filters - never hardcoded;
 *   - shown  = the number of product cards actually in the grid right now.
 * The "X of Y products" line is rebuilt from those two values after every
 * load, so it can never drift from what the customer can see.
 *
 * The browser URL is intentionally left untouched while loading more. Writing
 * ?page=N into the address bar made a reload / back / shared link open page N
 * on its own, hiding every product before it. If a customer does land on
 * ?page=N directly (old link, search engine), the earlier pages are restored
 * above it so the list is never missing products.
 *
 * Back / Forward restoration
 * --------------------------
 * Load More happens in memory only, so when the browser reloads the collection from the server
 * (Back from a product page, when the page is not served from the back/forward cache) the grid is
 * rebuilt with just the first batch. To make Back return to the same place:
 *   - every history entry of the collection gets a small id (merged into history.state, keeping the
 *     `searchParams` facets.js relies on), so state belongs to ONE entry - a fresh visit, a refresh
 *     or a new filter change is a different entry and can never inherit it;
 *   - sessionStorage keeps, per entry, how many pages were loaded, the product at the top of the
 *     viewport (and its offset), the scroll offset and the theme-chip row scroll,
 *     together with the filter / sort / collection key of the URL they belong to;
 *   - on Back/Forward the saved pages are fetched again through Shopify's own pagination (same URL,
 *     so filters, theme chips and sorting are applied), appended in order and de-duplicated, the count
 *     is rebuilt, and only then is the scroll position restored against the anchor product, re-checked
 *     while images settle, and abandoned the moment the customer scrolls.
 * If the browser serves the page from the back/forward cache, the DOM is already intact and none of
 * this runs. Nothing is restored on a normal visit or on refresh.
 */
(function () {
  if (window.customElements.get('pagination-component')) return;

  var COUNT_TOKEN = '__SHOWN__';
  var STATE_PREFIX = 'ttjCollectionState:';
  var STATE_INDEX = 'ttjCollectionStateIndex';
  var STATE_MAX_ENTRIES = 12;
  var STATE_MAX_AGE = 6 * 60 * 60 * 1000;
  var SETTLE_MAX_MS = 3000;
  var SETTLE_STABLE_MS = 500;

  // The first components are created while this script runs (initial page load); anything created
  // later comes from an AJAX re-render (filters, chips, sorting, history navigation).
  var initialPhase = true;
  window.setTimeout(function () {
    initialPhase = false;
  }, 0);

  function navigationType() {
    try {
      var entry = window.performance.getEntriesByType('navigation')[0];
      if (entry && entry.type) return entry.type;
      if (window.performance.navigation) return ['navigate', 'reload', 'back_forward'][window.performance.navigation.type] || 'navigate';
    } catch (error) {
      /* ignore */
    }
    return 'navigate';
  }

  /* Collection path + filters + sorting, without pagination transport params. */
  function currentUrlKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.sort();
    return window.location.pathname + '?' + params.toString();
  }

  function readEntryId() {
    var state = window.history.state;
    return state && typeof state === 'object' && state.ttjEntryId ? String(state.ttjEntryId) : '';
  }

  /*
   * Give the current history entry an id. facets.js reads `state.searchParams` on popstate (and treats a
   * null state as "initial URL"), so searchParams is always kept / filled in with the entry's own URL.
   */
  function ensureEntryId() {
    var existing = readEntryId();
    if (existing) return existing;
    var id = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    var next = {};
    var state = window.history.state;
    if (state && typeof state === 'object') next = Object.assign({}, state);
    if (typeof next.searchParams !== 'string') next.searchParams = window.location.search.slice(1);
    next.ttjEntryId = id;
    try {
      window.history.replaceState(next, '', window.location.href);
    } catch (error) {
      return '';
    }
    return id;
  }

  function readIndex() {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(STATE_INDEX) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function writeIndex(list) {
    try {
      window.sessionStorage.setItem(STATE_INDEX, JSON.stringify(list));
    } catch (error) {
      /* ignore */
    }
  }

  function removeState(id) {
    try {
      window.sessionStorage.removeItem(STATE_PREFIX + id);
    } catch (error) {
      /* ignore */
    }
    writeIndex(
      readIndex().filter(function (item) {
        return item.id !== id;
      }),
    );
  }

  function readState(id) {
    try {
      var state = JSON.parse(window.sessionStorage.getItem(STATE_PREFIX + id) || 'null');
      if (!state || state.v !== 1 || !state.key || Date.now() - state.time > STATE_MAX_AGE) return null;
      return state;
    } catch (error) {
      return null;
    }
  }

  function writeState(id, state) {
    try {
      window.sessionStorage.setItem(STATE_PREFIX + id, JSON.stringify(state));
    } catch (error) {
      return false; // storage full / unavailable: the collection simply behaves as before
    }
    var index = readIndex().filter(function (item) {
      return item.id !== id && Date.now() - item.time <= STATE_MAX_AGE;
    });
    index.push({ id: id, time: Date.now() });
    while (index.length > STATE_MAX_ENTRIES) {
      var oldest = index.shift();
      try {
        window.sessionStorage.removeItem(STATE_PREFIX + oldest.id);
      } catch (error) {
        /* ignore */
      }
    }
    writeIndex(index);
    return true;
  }

  /*
   * Viewport-relative top of an element from its LAYOUT position. Unlike getBoundingClientRect() this ignores
   * CSS transforms, so cards that are still in their reveal-on-scroll animation (translateY) do not skew it.
   */
  function layoutTop(element) {
    let top = 0;
    let node = element;
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top - (window.pageYOffset || document.documentElement.scrollTop || 0);
  }

  function scrollInstantly(top) {
    try {
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
    } catch (error) {
      window.scrollTo(0, Math.max(0, top));
    }
  }

  class PaginationComponent extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.isReady = false;
      this.abortController = null;
    }

    connectedCallback() {
      if (this.isReady) return;
      this.isReady = true;

      // NB: not `this.style` - that is the element's CSSStyleDeclaration, so assigning a
      // string to it never stored the pagination style and infinite scroll never started.
      this.paginationStyle = this.dataset.style;

      this.loadMoreButton = this.querySelector('[data-load-more-button]');
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', this.loadNextPage.bind(this));
      }

      if (this.paginationStyle === 'infinite_scroll') {
        this.sentinel = this.querySelector('[data-infinite-sentinel]');
        this.setUpObserver();
      }

      this.entryId = ensureEntryId();
      this.urlKey = currentUrlKey();
      this.canSave = false;
      this.userMoved = false;

      this.startUp();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
      this.stopSaving();
      this.stopWatchingUser();
    }

    /* ---------- saved collection state (Back / Forward) ---------- */

    /* Decide between a normal start and restoring this history entry's previously loaded pages. */
    startUp() {
      const saved = this.entryId ? readState(this.entryId) : null;
      let restore = false;

      if (saved && saved.key === this.urlKey && saved.pages > 1) {
        // Initial page load: only a real Back/Forward traversal restores (never a refresh or a fresh visit).
        // Later AJAX re-renders: an entry that already owns saved state can only be revisited via history.
        restore = initialPhase ? navigationType() === 'back_forward' : true;
      }
      if (saved && !restore) removeState(this.entryId);

      const previous = this.restorePreviousPages();

      if (!restore) {
        previous.then(() => this.startSaving());
        return;
      }

      // Take over scroll restoration only for the load we restore ourselves; the browser would otherwise
      // jump to its remembered offset inside the still-short first batch.
      const takeOverScroll = initialPhase;
      if (takeOverScroll) this.setScrollRestoration('manual');
      previous
        .then(() => this.restoreLoadedState(saved, takeOverScroll))
        .catch((error) => {
          if (!error || error.name !== 'AbortError') console.error(error);
        })
        .then(() => {
          if (takeOverScroll) this.setScrollRestoration('auto');
          if (this.isConnected) this.startSaving();
        });
    }

    setScrollRestoration(mode) {
      try {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = mode;
      } catch (error) {
        /* ignore */
      }
    }

    watchUser() {
      this.userMoved = false;
      this.onUserMove = () => {
        this.userMoved = true;
      };
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((name) => {
        window.addEventListener(name, this.onUserMove, { passive: true });
      });
    }

    stopWatchingUser() {
      if (!this.onUserMove) return;
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((name) => {
        window.removeEventListener(name, this.onUserMove);
      });
      this.onUserMove = null;
    }

    startSaving() {
      if (this.canSave) return;
      this.canSave = true;

      this.onPageHide = () => this.saveState();
      this.onVisibility = () => {
        if (document.visibilityState === 'hidden') this.saveState();
      };
      this.onClickCapture = (event) => {
        const grid = this.getGrid();
        if (!grid || !event.target || !event.target.closest) return;
        const link = event.target.closest('a[href]');
        if (link && grid.contains(link)) this.saveState();
      };
      this.onScroll = () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.saveState(), 250);
      };

      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    stopSaving() {
      if (!this.onPageHide) return;
      window.clearTimeout(this.scrollTimer);
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('click', this.onClickCapture, true);
      window.removeEventListener('scroll', this.onScroll);
      this.onPageHide = null;
      this.canSave = false;
    }

    /* Remember what is loaded and where the customer is, for THIS history entry only. */
    saveState() {
      if (!this.canSave || this.isLoading || !this.entryId) return;
      // A filter / chip / sort change pushes a new history entry (and URL) before this grid is replaced:
      // never write the old grid under the new entry.
      if (readEntryId() !== this.entryId || currentUrlKey() !== this.urlKey) return;

      const grid = this.getGrid();
      if (!grid || !grid.isConnected) return;

      const pages = parseInt(this.dataset.page, 10) || 1;
      if (pages <= 1) {
        removeState(this.entryId);
        return;
      }

      const items = this.getProductItems(grid);
      let anchorId = '';
      let anchorOffset = 0;
      for (let i = 0; i < items.length; i += 1) {
        const top = layoutTop(items[i]);
        if (top + items[i].offsetHeight > 1) {
          anchorId = items[i].dataset.productId;
          anchorOffset = top;
          break;
        }
      }

      const chips = document.querySelector('.ttj-theme-chips__list');
      writeState(this.entryId, {
        v: 1,
        key: this.urlKey,
        pages: pages,
        count: items.length,
        anchorId: anchorId,
        anchorOffset: anchorOffset,
        scrollY: window.pageYOffset || document.documentElement.scrollTop || 0,
        chipScroll: chips ? chips.scrollLeft : 0,
        time: Date.now(),
      });
    }

    /*
     * Re-load pages (currentPage+1 .. saved.pages) through Shopify's pagination for the current URL, append
     * them in order, rebuild the pagination state / count, then (optionally) restore the scroll position.
     */
    restoreLoadedState(saved, restoreScroll) {
      const grid = this.getGrid();
      if (!grid) return Promise.resolve();

      const firstPage = (parseInt(this.dataset.page, 10) || 1) + 1;
      const lastPage = saved.pages;

      this.isLoading = true;
      this.toggleLoadingUi(true);
      if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
      this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.watchUser();

      const requests = [];
      for (let page = firstPage; page <= lastPage; page += 1) {
        requests.push(
          this.fetchGridPage(this.buildSectionUrl(window.location.href, page, grid), grid).catch((error) => {
            if (error && error.name === 'AbortError') throw error;
            console.error(error);
            return null; // a failed page ends the restore at the last page that did load
          }),
        );
      }

      return Promise.all(requests)
        .then((results) => {
          if (!this.isConnected || this.getGrid() !== grid) return;

          let loadedPage = firstPage - 1;
          let lastComponent = null;
          for (let i = 0; i < results.length; i += 1) {
            const result = results[i];
            if (!result || !result.grid || !result.grid.children.length) break;
            this.appendItems(result.grid, grid, true);
            loadedPage = firstPage + i;
            lastComponent = result.component;
          }

          if (loadedPage >= firstPage) {
            this.dataset.page = String(loadedPage);
            this.dataset.nextUrl = (lastComponent && lastComponent.dataset.nextUrl) || '';
            if (lastComponent && lastComponent.dataset.total) this.dataset.total = lastComponent.dataset.total;
            if (lastComponent && lastComponent.dataset.countTemplate) {
              this.dataset.countTemplate = lastComponent.dataset.countTemplate;
            }
          }
          this.updateCount();

          if (!this.dataset.nextUrl) this.finish();
          else if (this.observer && this.sentinel) this.observer.observe(this.sentinel);

          if (restoreScroll && !this.userMoved) this.restoreScroll(saved, grid);

          const chips = document.querySelector('.ttj-theme-chips__list');
          if (chips && saved.chipScroll) chips.scrollLeft = saved.chipScroll;
        })
        .then(
          () => {
            this.isLoading = false;
            this.toggleLoadingUi(false);
            if (!this.settling) this.stopWatchingUser();
          },
          (error) => {
            this.isLoading = false;
            this.toggleLoadingUi(false);
            this.stopWatchingUser();
            throw error;
          },
        );
    }

    /*
     * Scroll back to the product that was at the top of the viewport, with the same offset, and keep correcting
     * for a short while as images lay out. Stops at once if the customer scrolls, taps or presses a key.
     */
    restoreScroll(saved, grid) {
      const selector = saved.anchorId ? '[data-product-id="' + String(saved.anchorId).replace(/[^\w-]/g, '') + '"]' : '';
      const anchor = selector ? grid.querySelector(selector) : null;
      const wantedOffset = saved.anchorOffset || 0;

      const place = () => {
        if (anchor && anchor.isConnected) {
          const delta = layoutTop(anchor) - wantedOffset;
          if (Math.abs(delta) > 1) scrollInstantly((window.pageYOffset || 0) + delta);
          return Math.abs(delta) > 1;
        }
        scrollInstantly(saved.scrollY || 0);
        return false;
      };

      place();

      // Images above the anchor that have not loaded AND have no reserved size can still push the anchor down.
      // (Cards reserve their image space with an aspect ratio, so normally nothing is ever pending.)
      const imagesPending = () => {
        if (!anchor || !anchor.isConnected) return false;
        const anchorTop = layoutTop(anchor);
        return Array.from(grid.querySelectorAll('img')).some((img) => {
          return !img.complete && img.offsetHeight < 2 && layoutTop(img) <= anchorTop + anchor.offsetHeight;
        });
      };

      this.settling = true;
      const started = window.performance.now();
      let stableSince = started;
      let done = false;
      const finishSettle = () => {
        if (done) return;
        done = true;
        this.settling = false;
        this.stopWatchingUser();
      };
      const tick = () => {
        if (done) return;
        const now = window.performance.now();
        if (this.userMoved || !this.isConnected || now - started > SETTLE_MAX_MS) return finishSettle();
        if (anchor && place()) stableSince = now;
        else if (now - stableSince > SETTLE_STABLE_MS && !imagesPending()) return finishSettle();
        window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
      // requestAnimationFrame is paused in background tabs: never leave listeners on.
      window.setTimeout(finishSettle, SETTLE_MAX_MS + 500);
    }

    setUpObserver() {
      if (!this.sentinel || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.loadNextPage();
          });
        },
        { rootMargin: '400px 0px 400px 0px' },
      );
      this.observer.observe(this.sentinel);
    }

    getGrid() {
      return document.getElementById(this.dataset.gridId || 'product-grid');
    }

    getProductItems(grid) {
      return Array.from(grid.children).filter((el) => el.dataset && el.dataset.productId);
    }

    getExistingProductIds(grid) {
      return new Set(this.getProductItems(grid).map((el) => el.dataset.productId));
    }

    /*
     * Build the Shopify section-rendering URL for a given page of the *current* result set.
     * `section_id` is replaced (never duplicated) and `page` is set explicitly, so the request
     * always returns just this section for exactly that page, with the filters kept.
     */
    buildSectionUrl(baseUrl, page, grid) {
      const url = new URL(baseUrl, window.location.origin);
      url.hash = '';
      url.searchParams.delete('section_id');
      if (page) url.searchParams.set('page', String(page));
      if (grid && grid.dataset.id) url.searchParams.set('section_id', grid.dataset.id);
      return url.toString();
    }

    fetchGridPage(url, grid) {
      return fetch(url, { signal: this.abortController ? this.abortController.signal : undefined })
        .then((response) => {
          if (!response.ok) throw new Error('Pagination request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return {
            grid: parsed.getElementById(grid.id),
            component: parsed.querySelector('pagination-component'),
          };
        });
    }

    toggleLoadingUi(isLoading) {
      if (this.loadMoreButton) {
        this.loadMoreButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.loadMoreButton.disabled = isLoading;
      }

      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);

      const infiniteLoading = this.querySelector('[data-infinite-loading]');
      if (infiniteLoading) infiniteLoading.hidden = !isLoading;
    }

    announce(message) {
      const status = this.querySelector('[data-pagination-status]');
      if (status) status.textContent = message;
    }

    /* Rebuild "X of Y products" from the real number of cards in the grid and Shopify's total. */
    updateCount() {
      const grid = this.getGrid();
      const countEl = this.querySelector('[data-load-more-count]');
      const template = this.dataset.countTemplate;
      if (!grid || !countEl || !template) return '';

      const total = parseInt(this.dataset.total, 10);
      let shown = this.getProductItems(grid).length;
      if (!isNaN(total)) shown = Math.min(shown, total);

      const text = template.split(COUNT_TOKEN).join(String(shown));
      countEl.textContent = text;
      return text;
    }

    /* Append products that are not already in the grid. Returns how many were added. */
    appendItems(sourceGrid, grid, skipReveal) {
      if (!sourceGrid) return 0;
      const existingIds = this.getExistingProductIds(grid);
      let added = 0;
      Array.from(sourceGrid.children).forEach((item) => {
        const productId = item.dataset ? item.dataset.productId : null;
        if (!productId || existingIds.has(productId)) return;
        existingIds.add(productId);
        // Restored cards must be visible straight away, not wait for a reveal-on-scroll animation delay.
        if (skipReveal) item.classList.add('scroll-trigger--cancel');
        grid.appendChild(item);
        added += 1;
      });
      return added;
    }

    loadNextPage() {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl || this.isLoading) return;

      const grid = this.getGrid();
      if (!grid) return;

      this.isLoading = true;
      // Stop observing while a request is in flight so we never fire duplicate requests.
      if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
      this.toggleLoadingUi(true);
      this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      this.fetchGridPage(this.buildSectionUrl(nextUrl, null, grid), grid)
        .then((result) => {
          // The filters may have changed while this request was in flight: this component and
          // grid were then replaced, so the response belongs to a result set nobody is viewing.
          if (!this.isConnected || this.getGrid() !== grid) return;

          const added = this.appendItems(result.grid, grid);

          if (result.component) {
            // Carry Shopify's fresh state forward: the real next page (empty on the last page),
            // the real current page and the real number of matching products.
            this.dataset.nextUrl = result.component.dataset.nextUrl || '';
            this.dataset.page = result.component.dataset.page || this.dataset.page;
            if (result.component.dataset.total) this.dataset.total = result.component.dataset.total;
            if (result.component.dataset.countTemplate) {
              this.dataset.countTemplate = result.component.dataset.countTemplate;
            }
          } else {
            this.dataset.nextUrl = '';
          }

          const countText = this.updateCount();
          this.isLoading = false;
          this.saveState();
          this.isLoading = true;

          if (!this.dataset.nextUrl) {
            this.finish();
          } else if (this.observer && this.sentinel) {
            this.observer.observe(this.sentinel);
          }

          this.announce(countText || added + ' more products loaded');
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error(error);
          // Let the customer retry (button) or let the scroll observer try again.
          if (this.observer && this.sentinel && this.dataset.nextUrl) this.observer.observe(this.sentinel);
        })
        .finally(() => {
          this.isLoading = false;
          this.toggleLoadingUi(false);
        });
    }

    /* Landed on ?page=N (N > 1): fetch pages 1..N-1 and put them above the current products. */
    restorePreviousPages() {
      const currentPage = parseInt(this.dataset.page, 10) || 1;
      if (currentPage <= 1) return Promise.resolve();

      const grid = this.getGrid();
      if (!grid) return Promise.resolve();

      this.isLoading = true;
      this.toggleLoadingUi(true);
      this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      const requests = [];
      for (let page = 1; page < currentPage; page += 1) {
        requests.push(this.fetchGridPage(this.buildSectionUrl(window.location.href, page, grid), grid));
      }

      return Promise.all(requests)
        .then((results) => {
          if (!this.isConnected || this.getGrid() !== grid) return;
          const existingIds = this.getExistingProductIds(grid);
          const fragment = document.createDocumentFragment();
          results.forEach((result) => {
            if (!result.grid) return;
            Array.from(result.grid.children).forEach((item) => {
              const productId = item.dataset ? item.dataset.productId : null;
              if (!productId || existingIds.has(productId)) return;
              existingIds.add(productId);
              fragment.appendChild(item);
            });
          });
          grid.insertBefore(fragment, grid.firstChild);
          this.updateCount();
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.toggleLoadingUi(false);
        });
    }

    /* Everything is loaded: remove the trigger, keep the final "Y of Y products" line. */
    finish() {
      if (this.observer) this.observer.disconnect();
      if (this.loadMoreButton) {
        this.loadMoreButton.remove();
        this.loadMoreButton = null;
      }
      const sentinel = this.querySelector('[data-infinite-sentinel]');
      if (sentinel) sentinel.remove();
      const infiniteLoading = this.querySelector('[data-infinite-loading]');
      if (infiniteLoading) infiniteLoading.remove();
      this.dataset.complete = 'true';
    }
  }

  window.customElements.define('pagination-component', PaginationComponent);
})();
